import React, { useEffect, useState } from 'react';
import { Alert, Button, Grid, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { exportPacientesExcel } from '../api/pacientesApi';
import { isAdminSession } from '../../../shared/auth/session';
import { buildDefaultEvaluationRanges } from '../utils/evaluationDateRanges';

export default function ExportPacientesExcel() {
  const navigate = useNavigate();
  const isAdmin = isAdminSession();
  const defaultRanges = buildDefaultEvaluationRanges();
  const [preStart, setPreStart] = useState(defaultRanges.preStart);
  const [preEnd, setPreEnd] = useState(defaultRanges.preEnd);
  const [postStart, setPostStart] = useState(defaultRanges.postStart);
  const [postEnd, setPostEnd] = useState(defaultRanges.postEnd);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/home', { replace: true });
    }
  }, [isAdmin, navigate]);

  const handleDownload = async () => {
    if (downloading || !isAdmin) return;

    const preStartDate = new Date(preStart);
    const preEndDate = new Date(preEnd);
    const postStartDate = new Date(postStart);
    const postEndDate = new Date(postEnd);

    if (!preStart || !preEnd || !postStart || !postEnd) {
      setError('Debe completar los rangos de fechas para PRE y POST.');
      return;
    }
    if (Number.isNaN(preStartDate.getTime()) || Number.isNaN(preEndDate.getTime())) {
      setError('Las fechas del rango PRE no son validas.');
      return;
    }
    if (Number.isNaN(postStartDate.getTime()) || Number.isNaN(postEndDate.getTime())) {
      setError('Las fechas del rango POST no son validas.');
      return;
    }
    if (preStartDate > preEndDate) {
      setError('La fecha inicial del rango PRE debe ser anterior o igual a la final.');
      return;
    }
    if (postStartDate > postEndDate) {
      setError('La fecha inicial del rango POST debe ser anterior o igual a la final.');
      return;
    }

    try {
      setError('');
      setDownloading(true);
      const response = await exportPacientesExcel({ preStart, preEnd, postStart, postEnd });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition && /filename="?([^";]+)"?/i.exec(disposition);
      link.setAttribute('download', match ? match[1] : 'pacientes_pre-post.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error('Error downloading Excel data', downloadError);
      setError('No se pudo descargar el archivo. Intente nuevamente.');
    } finally {
      setDownloading(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" gutterBottom color="#0D6AEE">
          Exportar Base de Datos en Excel
        </Typography>
        <Typography variant="body2">
          Defina los rangos de fecha correspondientes a las evaluaciones PRE y POST para generar la base de datos en Excel.
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Rango PRE</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Fecha inicio" type="date" value={preStart} onChange={(event) => setPreStart(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Fecha fin" type="date" value={preEnd} onChange={(event) => setPreEnd(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Rango POST</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Fecha inicio" type="date" value={postStart} onChange={(event) => setPostStart(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              <TextField label="Fecha fin" type="date" value={postEnd} onChange={(event) => setPostEnd(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Stack>
          </Paper>
        </Stack>
        <Button variant="contained" color="primary" disabled={downloading} onClick={handleDownload}>
          {downloading ? 'Descargando...' : 'Descargar Excel'}
        </Button>
      </Paper>
    </Grid>
  );
}
